import React, { useState, useEffect, useRef } from "react";
import { FcGoogle } from "react-icons/fc";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  LoginIcon,
  EmailSection,
  EmailInput,
  VerificationMessage,
  CodeInputContainer,
  CodeInput,
  ResendButton,
  ModalSubtitle,
  TitleContainer,
  ChevronButton,
  GoogleButton,
  LabelContainer,
  Label,
} from "./AuthModal.styles";
import { useDispatch, useSelector } from "react-redux";
import { sendCode, verifyEmail } from "../../store/slices/auth/asyncThunks";
import { toast } from "../modern-ui/sonner";
import {
  selectLoginPending,
  selectVerifyEmailLoading,
} from "../../store/slices/auth/slice";
import MailChecker from "mailchecker";
import {
  selectAffiliateRef,
  selectSource,
} from "../../store/slices/common/slice";
import TurnstileWidget from "../../components/utils/TurnstileWidget";
import { AUTH_TYPE } from "../../utils/constants";
import { useGoogleLogin } from "@react-oauth/google";
import Logo from "../Logo/Logo";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as EditSvg } from "../../assets/icons/edit.svg";
import Button from "../utils/Button/Button";
import CustomDiscord from "../utils/CustomDiscord/CustomDiscord";
import { useSearchParams } from "react-router-dom";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import TelegramLogin from "./TelegramLoginButton";

const AuthModal = ({ isOpen, onClose }) => {
  useLockBodyScroll(isOpen);
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [token, setToken] = useState(null);
  const [telegramData, setTelegramData] = useState(null);
  const [resending, setResending] = useState(false);
  const [showResendCaptcha, setShowResendCaptcha] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const loginLoading = useSelector(selectLoginPending);
  const verifyEmailLoading = useSelector(selectVerifyEmailLoading);

  const affiliateRef = useSelector(selectAffiliateRef);
  const source = useSelector(selectSource);
  const inputRefs = useRef([]);
  const turnstileRef = useRef(null);
  const resendTurnstileRef = useRef(null);
  // True only between an explicit "Resend" click and the send firing, so that
  // automatic widget re-runs (token refresh/reset) don't trigger extra sends.
  const resendPendingRef = useRef(false);

  const discordCodeParam = searchParams.get("code");
  const discordStateParam = searchParams.get("state");
  const authTrackingParams = {
    ...(affiliateRef && { affiliateRef }),
    ...(source && { source }),
  };

  const dispatch = useDispatch();

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      value = value[0];
    }

    if (!/^\d*$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // if (e.key === "Enter" && code.every((digit) => digit !== "")) {
    //   handleVerifyCode();
    // }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);

    if (!/^\d+$/.test(pastedData)) {
      return;
    }

    const newCode = [...code];
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);

    const nextEmptyIndex = newCode.findIndex((digit) => !digit);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const onGoogleSignUpClick = useGoogleLogin({
    flow: "auth-code",
    onSuccess: ({ code }) => {
      dispatch(
        sendCode({
          googleCode: code,
          authType: AUTH_TYPE.GOOGLE,
          ...authTrackingParams,
        }),
      )
        .unwrap()
        .then((response) => {
          if (response?.isNewUser) {
            setShowCodeInput(false);
            toast.success("Please set your username.");
            onClose();
          } else {
            toast.success("Successfully signed in with Google!");
            onClose();
          }
        })
        .catch((err) => {
          toast.error(
            err.message || "Google sign-in failed. Please try again.",
          );
        })
        .finally(() => {
          turnstileRef.current?.reset();
          setToken(null);
        });
    },
    onError: (err) => {
      toast.error("Google sign-in failed");
      console.error(err);
    },
  });

  const handleSendCode = () => {
    if (!email) {
      toast.error("Please enter an email address");
      return;
    }

    if (!MailChecker.isValid(email) || email.includes("+")) {
      toast.error("Please enter a valid email address");
      return;
    }

    dispatch(sendCode({ email, token, authType: AUTH_TYPE.BASIC }))
      .unwrap()
      .then((res) => {
        setShowCodeInput(true);
        setResendCooldown(60);
        toast.success(`Verification code sent to ${email}`);
      })
      .catch((err) => {
        if (
          err.message &&
          (err.message ===
            "Failed to send verification code. Please choose another email provider." ||
            err.message ===
              "Please wait 60 seconds before requesting a new verification code.")
        ) {
          toast.error(err.message);
        } else {
          toast.error(
            "Failed to send code. Please try again or use another email.",
          );
        }
      })
      .finally(() => {
        turnstileRef.current?.reset();
        setToken(null);
      });
  };

  useEffect(() => {
    const codeString = code.join("");
    if (codeString.trim().length === 6) {
      dispatch(
        verifyEmail({
          email,
          code: codeString,
          ...authTrackingParams,
        }),
      )
        .unwrap()
        .then((response) => {
          if (response?.data?.isNewUser) {
            setShowCodeInput(false);
            onClose();
            toast.success("Code verified! Please set your username.");
          } else {
            toast.success("Successfully signed in!");
            onClose();
          }
        })
        .catch((err) => {
          toast.error(err.message || "Invalid code. Please try again.");
        });
    }
    // eslint-disable-next-line
  }, [code]);

  const handleVerifyCode = () => {
    const codeString = code.join("");

    if (codeString.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }

    if (verifyEmailLoading) {
      return;
    }

    dispatch(
      verifyEmail({
        email,
        code: codeString,
        ...authTrackingParams,
      }),
    )
      .unwrap()
      .then((response) => {
        if (response?.data?.isNewUser) {
          setShowCodeInput(false);
          onClose();
          toast.success("Code verified! Please set your username.");
        } else {
          toast.success("Successfully signed in!");
          onClose();
        }
      })
      .catch((err) => {
        toast.error(err.message || "Invalid code. Please try again.");
      });
  };

  const handleDiscordError = (error) => {
    toast.error(error || "Discord login failed. Please try again.");
    turnstileRef.current?.reset();
    setToken(null);
  };

  const handleResendCode = () => {
    if (resending || resendCooldown > 0) {
      return;
    }

    setCode(["", "", "", "", "", ""]);

    // Mark this as a user-initiated resend so onSuccess knows to actually send.
    resendPendingRef.current = true;

    // Reveal the Turnstile widget on click. It verifies (silently for most
    // visitors, with a challenge for high-risk ones) and the resend itself
    // fires from onSuccess once we have a fresh, single-use token.
    if (!showResendCaptcha) {
      setShowResendCaptcha(true);
    } else {
      // Already shown: reset to mint a fresh token (tokens are single-use).
      resendTurnstileRef.current?.reset();
    }
  };

  const handleResendVerified = (freshToken) => {
    // Only send when an explicit resend click is pending. Turnstile re-runs its
    // challenge on token expiry/auto-refresh and would otherwise re-fire here,
    // causing a send loop that trips the server's rate limit.
    if (!resendPendingRef.current) {
      return;
    }
    resendPendingRef.current = false;

    setResending(true);
    dispatch(sendCode({ email, token: freshToken, authType: AUTH_TYPE.BASIC }))
      .unwrap()
      .then(() => {
        toast.success(`Verification code re-sent to ${email}`);
        setResendCooldown(60);
      })
      .catch((err) => {
        if (
          err?.message ===
            "Failed to send verification code. Please choose another email provider." ||
          err?.message ===
            "Please wait 60 seconds before requesting a new verification code."
        ) {
          toast.error(err.message);
        } else {
          toast.error("Failed to resend code. Please try again.");
        }
      })
      .finally(() => {
        // Do NOT reset the widget here — a reset re-runs the challenge, which
        // would re-fire onSuccess and send again. We reset on the next click.
        setResending(false);
      });
  };

  const handleBackToEmail = () => {
    setShowCodeInput(false);
    setCode(["", "", "", "", "", ""]);
    setShowResendCaptcha(false);
    setResendCooldown(0);
  };

  useEffect(() => {
    if (showCodeInput) {
      const raf = requestAnimationFrame(() => {
        inputRefs.current[0]?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [showCodeInput]);

  // Tick down the resend cooldown once per second.
  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (telegramData) {
      dispatch(
        sendCode({
          telegramData,
          authType: AUTH_TYPE.TELEGRAM,
          ...authTrackingParams,
        }),
      )
        .unwrap()
        .then((response) => {
          if (response?.isNewUser) {
            onClose();
            toast.success("Please set your username.");
          } else {
            onClose();
            setTelegramData(null);
            toast.success("Successfully signed in!");
          }
        })
        .catch((err) => {
          toast.error(err.message);
        })
        .finally(() => {
          setToken(null);
        });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telegramData]);

  useEffect(() => {
    if (discordCodeParam) {
      let discordTrackingParams = {};

      try {
        discordTrackingParams = JSON.parse(discordStateParam || "{}");
      } catch (error) {
        discordTrackingParams = {
          ...(discordStateParam && { affiliateRef: discordStateParam }),
        };
      }

      const finalAffiliate = affiliateRef || discordTrackingParams.affiliateRef;
      const finalSource = source || discordTrackingParams.source;

      dispatch(
        sendCode({
          discordCode: discordCodeParam,
          authType: AUTH_TYPE.DISCORD,
          ...(finalAffiliate && { affiliateRef: finalAffiliate }),
          ...(finalSource && { source: finalSource }),
        }),
      )
        .unwrap()
        .then((response) => {
          if (response?.isNewUser) {
            onClose();
            toast.success("Please set your username.");
          } else {
            onClose();
            toast.success("Successfully signed in!");
          }
        })
        .catch((err) => {
          toast.error(err.message);
        })
        .finally(() => {
          searchParams.delete("code");
          searchParams.delete("state");
          setSearchParams(searchParams);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discordCodeParam]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={() => {
        setEmail("");
        setCode(["", "", "", "", "", ""]);
        setShowCodeInput(false);
        setShowResendCaptcha(false);
        setResendCooldown(0);
      }}
    >
      <ModalContainer $width={showCodeInput}>
        <CloseButton
          onClick={() => {
            onClose();
            setTelegramData(null);
          }}
        >
          <CloseSvg />
        </CloseButton>
        {showCodeInput && (
          <ChevronButton
            onClick={() => {
              setShowCodeInput(false);
              setCode(["", "", "", "", "", ""]);
              setShowResendCaptcha(false);
              setResendCooldown(0);
            }}
          >
            <ChevronSvg />
          </ChevronButton>
        )}
        <ModalContent>
          <Logo />
          <TitleContainer>
            <ModalTitle>
              {showCodeInput ? "Enter Verification Code" : "Login"}
            </ModalTitle>
            {showCodeInput && (
              <>
                <ModalSubtitle>We sent a 6-digit code to</ModalSubtitle>
                <ModalSubtitle style={{ opacity: 1 }}>
                  {email}{" "}
                  <EditSvg
                    onClick={handleBackToEmail}
                    style={{ marginLeft: 8, cursor: "pointer" }}
                  />
                </ModalSubtitle>

                <ResendButton
                  onClick={handleResendCode}
                  style={{
                    pointerEvents:
                      resending || resendCooldown > 0 ? "none" : "auto",
                    opacity: resending || resendCooldown > 0 ? 0.4 : 0.6,
                  }}
                >
                  {resending
                    ? "Sending..."
                    : resendCooldown > 0
                      ? `Resend Code in ${resendCooldown}s`
                      : "Resend Code"}
                </ResendButton>
              </>
            )}
          </TitleContainer>
          {showCodeInput ? (
            <VerificationMessage>
              <CodeInputContainer>
                {code.map((digit, index) => (
                  <CodeInput
                    key={index}
                    disabled={verifyEmailLoading}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    maxLength={1}
                  />
                ))}
              </CodeInputContainer>
              <Button
                onClick={handleVerifyCode}
                disabled={code.some((digit) => !digit) || verifyEmailLoading}
                title={"Continue"}
                loading={verifyEmailLoading}
                style={{ width: "100%" }}
              />
              {showResendCaptcha && (
                <div style={{ width: "100%", marginTop: 12 }}>
                  <TurnstileWidget
                    ref={resendTurnstileRef}
                    siteKey="0x4AAAAAADZnRIH1bFizqKyI"
                    onSuccess={handleResendVerified}
                    onError={() => {
                      setResending(false);
                      toast.error("Verification failed");
                    }}
                    onExpire={() => setResending(false)}
                  />
                </div>
              )}
            </VerificationMessage>
          ) : (
            <>
              {!discordCodeParam && (
                <EmailSection>
                  <TelegramLogin
                    disabled={loginLoading}
                    onAuth={(data) => {
                      setTelegramData({ ...data, type: "V2" });
                    }}
                  />

                  <GoogleButton
                    onClick={onGoogleSignUpClick}
                    disabled={loginLoading}
                  >
                    <LoginIcon>
                      <FcGoogle size={18} />
                    </LoginIcon>
                    Log in with Google
                  </GoogleButton>
                  <CustomDiscord
                    onError={handleDiscordError}
                    disabled={loginLoading}
                    affiliateRef={affiliateRef}
                    source={source}
                  />
                  {!telegramData && (
                    <LabelContainer>
                      <Label>or use email address</Label>
                      <EmailInput
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={(e) =>
                          e.key === "Enter" && handleSendCode()
                        }
                      />
                    </LabelContainer>
                  )}
                </EmailSection>
              )}
              <TurnstileWidget
                ref={turnstileRef}
                siteKey="0x4AAAAAADZnRIH1bFizqKyI"
                onSuccess={(token) => {
                  console.log("Turnstile success token:", token);
                  setToken(token);
                }}
                onError={(error) => {
                  console.log("Turnstile error:", error);
                  toast.error("Verification failed");
                }}
                onExpire={() => {
                  console.log("Turnstile expired");
                  setToken(null);
                  toast.error("Verification expired");
                }}
              />
              <Button
                onClick={handleSendCode}
                disabled={!(email || telegramData)}
                title={"Send Verification Code"}
                loading={loginLoading}
                style={{ width: "100%" }}
              />
            </>
          )}
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default AuthModal;
